import React, { useEffect, useState } from 'react';
import { Box, Grid, MenuItem, Table, TableBody, TableContainer, TableHead, TableRow, TextField } from '@material-ui/core';
import Switch from 'react-switch';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Utils from '../../commons/utils/Utils.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles.js';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import InventarioTarifariosImportModal from './InventarioTarifariosImportModal.jsx';
import InventarioTarifariosHistorialModal from './InventarioTarifariosHistorialModal.jsx';

const BASE_URL = '/inventario/tarifarios';

const PAGINATION_INIT = { page: 0, count: 0, perPage: 10 };

const FILTROS_INIT = {
  numeroDeParte: '',
  descripcion: '',
  tipo: '',
  categoria: '',
  verInactivos: false,
  soloProveedores: false,
};

const TIPOS = [
  { value: 'Producto', id: 'inventarioTarifarios.items.tipo_producto', label: 'Producto' },
  { value: 'Mano De Obra', id: 'inventarioTarifarios.items.tipo_mano_obra', label: 'Mano de Obra' },
];

/** null y '' se muestran vacíos (= "Catálogo"). Un 0 también es "Catálogo": el backend lo devuelve null. */
const valorDelInput = (valor) => (valor === null || valor === undefined ? '' : String(valor));

/**
 * VEC-3499 — sección "Ítems del Catálogo" del ABM de un tarifario.
 *
 * El tarifario nace vacío y el primer import (que exige todos los ítems del Catálogo) lo carga
 * completo. A partir de ahí los ítems no se agregan ni se quitan: solo se editan sus valores, por
 * pantalla o importando la planilla, que se baja desde el popup de Importar.
 * El botón Exportar baja solo los ítems que muestra la grilla, con los filtros aplicados.
 * Los ítems inactivados en el Catálogo se ocultan por defecto; con "Ver inactivos" aparecen
 * grisados y de solo lectura (el backend también rechaza editarlos).
 *
 * Las ediciones no se guardan acá: se acumulan en `cambios`, que vive en el ABM para sobrevivir a la
 * paginación y a los filtros, y viajan con el Guardar del formulario.
 * El import, en cambio, escribe en el momento; por eso no se habilita con cambios sin guardar.
 */
const InventarioTarifariosItems = ({ tarifarioId, tarifarioNombre, isView, puedeEditar, cambios, onCambio }) => {
  const intl = useIntl();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  // El tarifario nace vacío y se carga con el primer import: hasta entonces no hay nada que exportar.
  const [tarifarioVacio, setTarifarioVacio] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [pagination, setPagination] = useState(PAGINATION_INIT);
  const [filtros, setFiltros] = useState(FILTROS_INIT);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_INIT);
  const [categorias, setCategorias] = useState([]);

  const editable = !isView && puedeEditar;
  const hayPendientes = Object.keys(cambios).length > 0;
  // Nº de parte, Descripción, Tipo, Categoría, Costo Catálogo, Costo, Valor máximo, Costo fijo y la
  // columna sin título del aviso de ítem inactivo.
  const columnCount = 9;
  // Sin proveedores habilitados en el cliente el filtro no tiene sentido: se muestra griseado y en false.
  const proveedoresHabilitados = ConfigBusiness.get('servicios.proveedores.habilitado') === 'true';

  // Categorías de los ítems del Catálogo, inactivos incluidos: se pueden filtrar con "Ver inactivos".
  const fetchCategorias = () => {
    Utils.getData(`${BASE_URL}/${tarifarioId}/items/select/categorias`)
      .then((data) => setCategorias(data || []))
      .catch(() => setCategorias([]));
  };

  useEffect(() => {
    fetchCategorias();
  }, [tarifarioId]);

  useEffect(() => {
    fetchRows();
  }, [tarifarioId, pagination.page, pagination.perPage, filtrosAplicados]);

  // Los switches viajan siempre como 'true'/'false': el `|| ''` de los textos mandaría un false vacío.
  const queryDeFiltros = () =>
    Object.keys(FILTROS_INIT)
      .map((key) => {
        const valor = filtrosAplicados[key];
        return key + '=' + encodeURIComponent(typeof valor === 'boolean' ? String(valor) : valor || '');
      })
      .join('&');

  const fetchRows = () => {
    setLoading(true);
    // +1 porque la API pagina en base 1 y el front en base 0.
    const query = '?page=' + (pagination.page + 1) + '&perPage=' + pagination.perPage + '&' + queryDeFiltros();

    Utils.getData(`${BASE_URL}/${tarifarioId}/items/grid${query}`)
      .then((data) => {
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setTarifarioVacio(!!(data && data.tarifarioVacio));
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  const buscar = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setFiltrosAplicados({ ...filtros });
  };

  const exportar = () => {
    setExportando(true);
    // Con los filtros APLICADOS (los de la última búsqueda, no los que se están tipeando): sale lo
    // mismo que muestra la grilla, sin paginar. La planilla completa para importar es /items/plantilla.
    Utils.dataBlob(`${BASE_URL}/${tarifarioId}/items/exportar?${queryDeFiltros()}`, 'GET', {})
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        // dataBlob devuelve el filename tal como viene del header, con las comillas incluidas.
        link.setAttribute('download', String(res.filename).replace(/"/g, ''));
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      // Utils.dataBlob ya avisa el error por Swal; el catch solo deja correr el finally.
      .catch(() => {})
      .finally(() => setExportando(false));
  };

  /** Lo que se ve en la fila: el cambio pendiente si lo hay; si no, lo que vino del servidor. */
  const estadoDe = (row) =>
    cambios[row.itemId] || { costo: row.costo, valorMaximo: row.valorMaximo, costoFijo: row.costoFijo };

  const cambiar = (row, campo, valor) => {
    onCambio(row.itemId, { ...estadoDe(row), [campo]: valor });
  };

  const formatMonto = (valor) =>
    valor === null || valor === undefined ? '-' : LocaleFormat.formatNumber(Number(valor), 2);

  // Un ítem inactivo en el Catálogo no se edita, aunque el tarifario sí sea editable.
  const filaEditable = (row) => editable && row.activo !== false;

  const renderMonto = (row, campo) => {
    const valor = estadoDe(row)[campo];
    if (!filaEditable(row)) {
      return valor === null || valor === undefined || valor === ''
        ? intl.formatMessage({ id: 'inventarioTarifarios.items.catalogo', defaultMessage: 'Catálogo' })
        : formatMonto(valor);
    }
    return (
      <input
        type="number"
        min="0"
        step="0.01"
        className="form-control form-control-sm text-right"
        style={{ maxWidth: '130px', marginLeft: 'auto' }}
        placeholder={intl.formatMessage({ id: 'inventarioTarifarios.items.catalogo', defaultMessage: 'Catálogo' })}
        value={valorDelInput(valor)}
        onChange={(e) => cambiar(row, campo, e.target.value)}
      />
    );
  };

  const renderFiltroTexto = (campo, messageId, label) => (
    <div className="col-md-3">
      <TextField
        fullWidth
        label={intl.formatMessage({ id: messageId, defaultMessage: label })}
        value={filtros[campo]}
        onChange={(e) => setFiltros({ ...filtros, [campo]: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            buscar();
          }
        }}
      />
    </div>
  );

  const tituloImportar = hayPendientes
    ? intl.formatMessage({
        id: 'inventarioTarifarios.items.importar_con_pendientes',
        defaultMessage: 'Guarde o descarte los cambios de los ítems antes de importar',
      })
    : intl.formatMessage({ id: 'inventarioTarifarios.items.importar', defaultMessage: 'Importar' });

  return (
    <>
      <div className="d-flex justify-content-between align-items-start">
        <p className="text-muted mb-1">
          <FormattedMessage
            id="inventarioTarifarios.items.ayuda"
            defaultMessage="Solo se registran montos (Costo, Valor máximo) y Costo fijo. Los ítems se crean, editan e inactivan únicamente en el Catálogo de Ítems. Un monto vacío significa que se usa el valor del Catálogo. Para cargar valores por planilla, descárguela desde Importar, complétela e impórtela."
          />
        </p>
        {/* Mismos botones grises e iconos que el resto de las grillas del proyecto
            (src/commons/grid/GridToolbar.jsx): ft-download para importar, ft-upload para exportar. */}
        <GridToolbar>
          {editable && (
            <GridToolbarButton
              title={tituloImportar}
              onClick={() => setImportOpen(true)}
              disabled={hayPendientes}
              icon="ft-download"
            />
          )}
          <GridToolbarButton
            messageId="inventarioTarifarios.items.exportar"
            label="Exportar los ítems del tarifario con los filtros aplicados"
            onClick={exportar}
            disabled={exportando || tarifarioVacio}
            icon={exportando ? 'la la-spinner spinner' : 'ft-upload'}
          />
          {/* Def. 9: el historial es de consulta, así que está también en modo Ver. */}
          <GridToolbarButton
            messageId="inventarioTarifarios.items.historial"
            label="Historial del tarifario"
            onClick={() => setHistorialOpen(true)}
            icon="la la-history"
          />
        </GridToolbar>
      </div>

      <div className="row mb-1 align-items-end">
        {renderFiltroTexto('numeroDeParte', 'inventarioTarifarios.items.filtro_numero_parte', 'Nº de parte')}
        {renderFiltroTexto('descripcion', 'inventarioTarifarios.items.filtro_descripcion', 'Descripción')}
        <div className="col-md-2">
          <TextField
            select
            fullWidth
            label={intl.formatMessage({ id: 'inventarioTarifarios.items.filtro_tipo', defaultMessage: 'Tipo' })}
            value={filtros.tipo}
            onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
          >
            <MenuItem value="">
              {intl.formatMessage({ id: 'inventarioTarifarios.items.todos', defaultMessage: 'Todos' })}
            </MenuItem>
            {TIPOS.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {intl.formatMessage({ id: t.id, defaultMessage: t.label })}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <div className="col-md-2">
          <TextField
            select
            fullWidth
            label={intl.formatMessage({ id: 'inventarioTarifarios.items.filtro_categoria', defaultMessage: 'Categoría' })}
            value={filtros.categoria}
            onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
          >
            <MenuItem value="">
              {intl.formatMessage({ id: 'inventarioTarifarios.items.todas', defaultMessage: 'Todas' })}
            </MenuItem>
            {categorias.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      {/* Mismo patrón que los switches de InventarioItemsGridFiltros: se aplican con Buscar. */}
      <Grid container item xs={12} spacing={2} alignItems="center" className="mb-1">
        <Grid component={Box} item xs="auto" style={{ paddingRight: 20 }}>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <Switch
              onChange={(value) => setFiltros({ ...filtros, verInactivos: value })}
              checked={filtros.verInactivos}
              offColor="#FF4961"
              onColor="#28D094"
            />
            <FormattedMessage id="inventarioTarifarios.items.filtro_ver_inactivos" defaultMessage="Ver inactivos" />
          </div>
        </Grid>
        <Grid component={Box} item xs="auto" style={{ paddingRight: 20 }}>
          <div className="d-flex align-items-center" style={{ gap: 8, opacity: proveedoresHabilitados ? 1 : 0.5 }}>
            <Switch
              onChange={(value) => setFiltros({ ...filtros, soloProveedores: value })}
              checked={proveedoresHabilitados && filtros.soloProveedores}
              disabled={!proveedoresHabilitados}
              offColor="#FF4961"
              onColor="#28D094"
            />
            <FormattedMessage
              id="inventarioTarifarios.items.filtro_solo_proveedores"
              defaultMessage="Solo ver ítems de proveedores asignados"
            />
          </div>
        </Grid>
        <Grid component={Box} item style={{ flex: 1 }} />
        <Grid component={Box} item xs="auto">
          <button type="button" className="btn btn-primary" onClick={buscar}>
            <i className="fa fa-search"></i>{' '}
            {intl.formatMessage({ id: 'inventarioTarifarios.items.buscar', defaultMessage: 'Buscar' })}
          </button>
        </Grid>
      </Grid>

      <TableContainer>
        <Table aria-label="inventario tarifarios items table" className="table-hover">
          <TableHead>
            <TableRow>
              <SortableCell field="numeroDeParte" messageId="inventarioTarifarios.items.column_numero_parte" label="Nº de parte" />
              <SortableCell field="descripcion" messageId="inventarioTarifarios.items.column_descripcion" label="Descripción" />
              <SortableCell field="tipo" messageId="inventarioTarifarios.items.column_tipo" label="Tipo" />
              <SortableCell field="categoria" messageId="inventarioTarifarios.items.column_categoria" label="Categoría" />
              <SortableCell field="costoCatalogo" align="right" messageId="inventarioTarifarios.items.column_costo_catalogo" label="Costo Catálogo" />
              <SortableCell field="costo" align="right" messageId="inventarioTarifarios.items.column_costo" label="Costo" />
              <SortableCell field="valorMaximo" align="right" messageId="inventarioTarifarios.items.column_valor_maximo" label="Valor máximo" />
              <SortableCell field="costoFijo" align="center" messageId="inventarioTarifarios.items.column_costo_fijo" label="Costo fijo" />
              {/* Columna sin título: solo aloja el aviso de ítem inactivo. */}
              <StyledTableCell style={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            <GridLoadingRow visible={loading} columns={columnCount} />
            {!loading &&
              (rows.length > 0 ? (
                rows.map((row) => {
                  const tipo = TIPOS.find((t) => t.value === row.tipo);
                  return (
                    <TableRow key={'TarifarioItem_' + row.itemId} style={row.activo === false ? { opacity: 0.5 } : undefined}>
                      <StyledTableCell>{row.numeroDeParte || '-'}</StyledTableCell>
                      <StyledTableCell>{row.descripcion}</StyledTableCell>
                      <StyledTableCell>{tipo ? intl.formatMessage({ id: tipo.id, defaultMessage: tipo.label }) : row.tipo}</StyledTableCell>
                      <StyledTableCell>{row.categoria || '-'}</StyledTableCell>
                      <StyledTableCell align="right">{formatMonto(row.costoCatalogo)}</StyledTableCell>
                      <StyledTableCell align="right">{renderMonto(row, 'costo')}</StyledTableCell>
                      <StyledTableCell align="right">{renderMonto(row, 'valorMaximo')}</StyledTableCell>
                      <StyledTableCell align="center">
                        <Switch
                          onChange={(estado) => cambiar(row, 'costoFijo', estado)}
                          checked={estadoDe(row).costoFijo ? true : false}
                          disabled={!filaEditable(row)}
                          height={20}
                          width={42}
                          offColor="#FF4961"
                          onColor="#28D094"
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.activo === false && (
                          <StyledTooltip
                            title={intl.formatMessage({
                              id: 'inventarioTarifarios.items.item_inactivo',
                              defaultMessage: 'Este ítem fue inactivado en el Catálogo',
                            })}
                          >
                            <i className="fa fa-exclamation-triangle text-warning"></i>
                          </StyledTooltip>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  );
                })
              ) : (
                <GridEmptyRow
                  columns={columnCount}
                  messageId={tarifarioVacio ? 'inventarioTarifarios.items.tarifario_vacio' : 'inventarioTarifarios.items.sin_resultados'}
                  label={
                    tarifarioVacio
                      ? 'El tarifario no tiene ítems. Descargue la plantilla desde Importar, complétela e impórtela.'
                      : 'No hay ítems que coincidan con los filtros.'
                  }
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <GridPagination
        count={count}
        page={pagination.page}
        perPage={pagination.perPage}
        onPageChange={(event, newPage) => setPagination({ ...pagination, page: newPage })}
        onPerPageChange={(event) => setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 })}
      />

      <InventarioTarifariosHistorialModal
        open={historialOpen}
        tarifarioId={tarifarioId}
        tarifarioNombre={tarifarioNombre}
        onClose={() => setHistorialOpen(false)}
      />

      {editable && (
        <InventarioTarifariosImportModal
          open={importOpen}
          tarifarioId={tarifarioId}
          onClose={() => setImportOpen(false)}
          onImportado={() => {
            fetchRows();
            // El import puede asignar ítems de categorías que el combo todavía no tenía.
            fetchCategorias();
          }}
        />
      )}
    </>
  );
};

export default InventarioTarifariosItems;
